import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bell,
  ChevronDown,
  HelpCircle,
  LogOut,
  Settings,
  ShieldCheck,
} from 'lucide-react';

import { BrixLogo } from '@/components/brand/BrixLogo';
import { NotificationsBell } from '@/components/admin/NotificationsBell';
import { useCustomer } from '@/hooks/useCustomer';
import { useIsSuperadmin } from '@/hooks/admin/useIsSuperadmin';
import { signOut, useSession } from '@/lib/auth';
import { initials } from '@/lib/format';

/**
 * App header. Account context is switched via the sidebar account switcher
 * (top-left); there's no per-location picker here — each customer account is its
 * own account, so the ordering location is resolved from the account in AppShell.
 */
export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { session } = useSession();
  const { data: ctx } = useCustomer();
  const { isSuperadmin } = useIsSuperadmin();
  const email = session?.user?.email ?? '';

  const fullName = ctx?.user.fullName ?? session?.user?.email ?? '';
  const roleLabel =
    ctx?.user.role === 'ap'
      ? 'Accounts Payable'
      : ctx?.user.role === 'admin'
        ? 'Admin'
        : 'Member';

  return (
    <header
      data-app-chrome
      className="relative h-16 flex items-center justify-between px-6"
      style={{
        background:
          'linear-gradient(to right, #FFFFFF 0%, #FFFFFF 24%, var(--brix-primary) 62%)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
      }}
    >
      {/* Left: Brix logo + Powered by Alameda Soda Co. lockup, sitting in the
          white half of the white → navy gradient. */}
      <div className="relative flex items-center">
        <BrixLogo variant="full" size="md" showTagline />
      </div>

      {/* Right: bell + profile, on the dark grey side of the gradient. */}
      <div className="relative flex items-center gap-3">
        {/* Past-due / status chip removed per Sky — past-due now surfaces as a
            pop-up alert on /home (see PastDueAlert in src/pages/Home.tsx). */}

        {/* Account / sub-account switcher lives ONLY in the sidebar (top-left).
            The top-bar mirror was removed per Sky. */}

        {/* Superadmins get the live action-items bell (applications, change
            requests, EDI review, audits, payments); customers keep the plain
            bell until a customer-facing feed exists. */}
        {isSuperadmin ? (
          <NotificationsBell />
        ) : (
          <button
            type="button"
            aria-label="Notifications"
            className="w-8 h-8 rounded-lg flex items-center justify-center border border-white/10 bg-white/[0.06] backdrop-blur-md hover:bg-white/[0.12] hover:border-white/15 transition-all duration-200 cursor-pointer"
          >
            <Bell size={16} strokeWidth={1.75} className="text-white" />
          </button>
        )}

        <div className="relative pl-3 border-l border-white/10">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            className="flex items-center gap-2 px-1 py-1 rounded-lg hover:bg-white/[0.08] transition-colors duration-200 cursor-pointer"
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold bg-red-600 text-white relative overflow-hidden"
              aria-hidden
            >
              <span className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent pointer-events-none" />
              <span className="relative">{initials(fullName)}</span>
            </div>
            <div className="hidden sm:block text-left">
              <p className="text-[14px] text-white font-medium leading-none truncate max-w-[160px]">
                {fullName || 'Sign in'}
              </p>
              <p className="text-[12px] opacity-60 text-white leading-none mt-0.5">
                {roleLabel}
              </p>
            </div>
            <ChevronDown
              size={15}
              className="text-white/70 transition-transform duration-200 ml-0.5"
              style={{ transform: menuOpen ? 'rotate(180deg)' : 'none' }}
            />
          </button>

          {menuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                aria-hidden
                onClick={() => setMenuOpen(false)}
              />
              <div
                role="menu"
                className="absolute top-full right-0 mt-2 w-60 z-50 rounded-xl border border-brix-border bg-brix-surface/95 backdrop-blur-xl shadow-card-hover overflow-hidden"
              >
                <div className="px-4 py-3.5 border-b border-brix-border bg-brix-surface-alt/40">
                  <p className="text-base font-semibold text-brix-ink truncate">
                    {fullName || 'Signed in'}
                  </p>
                  {email && (
                    <p className="text-[13px] text-brix-muted truncate mt-0.5">{email}</p>
                  )}
                  <p className="text-[12px] tracking-tight text-brix-primary font-semibold mt-1.5">
                    {roleLabel}
                  </p>
                </div>

                <nav className="py-1">
                  <Link
                    to="/settings"
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 px-4 py-2.5 text-base text-brix-ink hover:bg-brix-primary-tint/60 cursor-pointer transition-colors"
                  >
                    <Settings size={16} className="text-brix-muted" /> Settings
                  </Link>
                  <Link
                    to="/guide"
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 px-4 py-2.5 text-base text-brix-ink hover:bg-brix-primary-tint/60 cursor-pointer transition-colors"
                  >
                    <HelpCircle size={16} className="text-brix-muted" /> Help
                  </Link>
                  {isSuperadmin && (
                    <Link
                      to="/admin"
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-base text-brix-ink hover:bg-brix-primary-tint/60 cursor-pointer transition-colors"
                    >
                      <ShieldCheck size={16} className="text-brix-primary" /> Admin settings
                    </Link>
                  )}
                </nav>

                <div className="border-t border-brix-border py-1">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      void signOut();
                    }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-base text-brix-ink hover:bg-brix-primary-tint/60 cursor-pointer transition-colors"
                  >
                    <LogOut size={16} className="text-brix-muted" /> Sign out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
